import { useEffect } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
import { useFullPlaylist } from '../../savedItems/savedItemsQueries';
import { useCanUserEditPlaylist } from '../../savedItems/savedItemsHooks';
import { usePlaylistItemManager } from './PlaylistItemManager';
import produce from 'immer';
import { RESULTS_URL_FRAGMENT } from '../../../constants/Navigation';
import UsageTracker from '../../../utils/UsageTracker';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const SEARCH_QUERY_KEY = FILTER_TYPES.SEARCH;
export const PLAYLIST_ITEM_SEARCH_PATH = RESULTS_URL_FRAGMENT;
const noop = () => {};
const PlaylistItemSearchContext = /*#__PURE__*/createContext({
  query: new URLSearchParams(window.location.search).get(SEARCH_QUERY_KEY) || '',
  resetQuery: noop,
  playlistId: 0,
  newPlaylistItems: [],
  playlist: null,
  addPlaylistItem: noop,
  removePlaylistItem: noop,
  resetPlaylistItems: noop,
  saveNewPlaylistItems: () => Promise.resolve({}),
  resetAllState: noop,
  playlistLoading: false,
  updatePlaylistLoading: false
});
export const PlaylistItemSearchProvider = ({
  children,
  playlistId
}) => {
  const {
    saveNewPlaylistItems: savePlaylistItems,
    updatePlaylistLoading
  } = usePlaylistItemManager();
  const {
    playlist,
    playlistLoading
  } = useFullPlaylist(playlistId);
  const [query, setQuery] = useState(new URLSearchParams(window.location.search).get(SEARCH_QUERY_KEY) || '');
  const [newPlaylistItems, setPlaylistItems] = useState([]);
  useEffect(() => {
    if (playlist && playlist.items.length > 0) {
      const items = playlist.items.map(item => Object.assign({}, item, {
        hasBeenRemoved: false
      }));
      setPlaylistItems(items);
    }
  }, [playlist]);
  const resetQuery = useCallback(() => {
    setQuery('');
  }, [setQuery]);
  const addPlaylistItem = useCallback(newOrUpdatedItem => {
    setPlaylistItems(items => produce(items, draft => {
      let foundItem = false;
      for (const item of draft) {
        if (item.itemId === newOrUpdatedItem.itemId && item.type === newOrUpdatedItem.type) {
          item.hasBeenRemoved = false;
          foundItem = true;
          break;
        }
      }
      if (!foundItem) draft.push(newOrUpdatedItem);
    }));
    UsageTracker.track('playlistInteraction', {
      action: 'add-to-playlist',
      location: 'playlist-items-search',
      visibility: newOrUpdatedItem.visibility ? readUnhandledEnumCase(newOrUpdatedItem.visibility) : undefined
    });
  }, []);
  const removePlaylistItem = useCallback(itemToRemove => {
    setPlaylistItems(items => produce(items, draft => {
      for (const item of draft) {
        if (item.itemId === itemToRemove.itemId && item.type === itemToRemove.type) {
          item.hasBeenRemoved = true;
          break;
        }
      }
    }));
    UsageTracker.track('playlistInteraction', {
      action: 'remove-item-from-playlist',
      location: 'playlist-items-search',
      isCustom: itemToRemove.visibility === 'CREATOR_PORTAL_ONLY'
    });
  }, []);
  const resetPlaylistItems = useCallback(() => {
    setPlaylistItems(playlist ? playlist.items.map(item => Object.assign({}, item, {
      hasBeenRemoved: false
    })) : []);
  }, [playlist]);
  const resetAllState = useCallback(() => {
    resetPlaylistItems();
    resetQuery();
  }, [resetPlaylistItems, resetQuery]);
  const saveNewPlaylistItems = useCallback(() => {
    return savePlaylistItems(newPlaylistItems).then(result => {
      if (!(result !== null && result !== void 0 && result.errors)) {
        resetQuery();
      }
      return result;
    });
  }, [newPlaylistItems, resetQuery, savePlaylistItems]);
  const contextState = useMemo(() => ({
    query,
    resetQuery,
    playlistId,
    newPlaylistItems,
    playlist,
    addPlaylistItem,
    removePlaylistItem,
    saveNewPlaylistItems,
    resetPlaylistItems,
    resetAllState,
    playlistLoading,
    updatePlaylistLoading
  }), [addPlaylistItem, newPlaylistItems, playlist, playlistId, query, removePlaylistItem, resetAllState, saveNewPlaylistItems, resetPlaylistItems, resetQuery, playlistLoading, updatePlaylistLoading]);
  return /*#__PURE__*/_jsx(PlaylistItemSearchContext.Provider, {
    value: contextState,
    children: children
  });
};
export const usePlaylistItemSearch = () => {
  const contextState = useContext(PlaylistItemSearchContext);
  const {
    newPlaylistItems,
    playlist
  } = contextState;
  const itemsAdded = newPlaylistItems.filter(item => !item.hasBeenRemoved && !(playlist !== null && playlist !== void 0 && playlist.items.some(originalItem => originalItem.itemId === item.itemId && originalItem.type === item.type)));
  const itemsRemoved = newPlaylistItems.filter(item => item.hasBeenRemoved && (playlist === null || playlist === void 0 ? void 0 : playlist.items.some(originalItem => originalItem.itemId === item.itemId && originalItem.type === item.type)));
  return Object.assign({}, contextState, {
    numberOfSelectedItems: newPlaylistItems.filter(item => !item.hasBeenRemoved).length,
    itemsAdded,
    itemsAddedCount: itemsAdded.length,
    itemsRemoved,
    itemsRemovedCount: itemsRemoved.length
  });
};
export const PlaylistItemSearchManager = ({
  listView,
  searchView
}) => {
  const {
    url
  } = useRouteMatch();
  const {
    playlistId
  } = usePlaylistItemSearch();
  const userCanEditPlaylist = useCanUserEditPlaylist(playlistId);
  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsxs(Route, {
      path: `*${PLAYLIST_ITEM_SEARCH_PATH}`,
      children: [userCanEditPlaylist !== null && !userCanEditPlaylist && /*#__PURE__*/_jsx(Redirect, {
        to: url
      }), searchView]
    }), /*#__PURE__*/_jsx(Route, {
      children: listView
    })]
  });
};